import React, { useContext, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Project } from 'types/Project'
import ParticipantCard from 'components/ParticipantProfile/ParticipantCard'
import addNoteAPI from 'api/Participant/AddNote/Action'
import { useAlert } from 'components/Shared/Alert'
import AssignProject from 'components/ParticipantProfile/AddNote/AssignProject'
import { ParticipantProfileContext } from 'containers/ParticipantProfile'

const ParticipantNote = () => {
  const { updateNewNoteCount, setProjectIdForNotesFilter } = useContext(
    ParticipantProfileContext,
  ).functions
  const [note, setNote] = useState('')
  const [focus, setFocus] = useState(false)
  const [saving, onSaving] = useState(false)
  const [error, onError] = useState(false)
  const [assignedProject, setAssignedProject] = useState<Project | null>(null)

  const { id } = useParams()
  const { t } = useTranslation()
  const { showAlert } = useAlert()

  const handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setNote(target.value)

    error && onError(false)
  }

  const handleAssignProject = (value: Project | null) => {
    setAssignedProject(value)
  }

  const handleReset = () => {
    setFocus(false)
    onSaving(false)
    setNote('')
    onError(false)
    setAssignedProject(null)
  }

  const showErrorAlert = () => {
    showAlert({
      title: t('participant.note.errorMessage'),
    })

    onSaving(false)
  }

  const getRequestPayload = () => {
    return {
      participantId: id as string,
      payload: {
        data: {
          attributes: {
            note: {
              content: note,
              ...(assignedProject && { project_id: assignedProject.id }),
            },
          },
        },
      },
    }
  }

  const addNoteRequest = async () => {
    const requestPayload = getRequestPayload()

    const { addNote } = addNoteAPI
    const response = await addNote(requestPayload)

    if (response.code) {
      showErrorAlert()
      return
    }

    handleReset()
    setProjectIdForNotesFilter('')
    updateNewNoteCount()
  }

  const handleAdd = () => {
    if (!note) {
      onError(true)

      return
    }

    onSaving(true)
    addNoteRequest()
  }

  const buttonDefaultText = t('common.add')
  const buttonLoadingText = (
    <>
      <CircularProgress size={20} sx={{ mr: 1 }} />
      {buttonDefaultText}
    </>
  )

  const buttonText = saving ? buttonLoadingText : buttonDefaultText

  return (
    <ParticipantCard>
      <Typography variant="h5" component="h5" mb={3} data-testid="note-title">
        {t('participant.note.title')}
      </Typography>

      <TextField
        multiline
        value={note}
        onChange={handleChange}
        variant="filled"
        minRows={2}
        placeholder={t('participant.note.placeholder')}
        onFocus={() => setFocus(true)}
        fullWidth
        sx={{ mb: assignedProject ? 3 : 1 }}
        error={error}
        helperText={
          error && (
            <span data-testid="note-empty-error">
              {t('participant.note.helpText')}
            </span>
          )
        }
        data-testid="note-text"
      />

      {focus && (
        <>
          <AssignProject onChange={handleAssignProject} />

          <Box mt={3} textAlign={'right'} data-testid="note-actions">
            <Button
              sx={{ mr: 3 }}
              onClick={handleReset}
              data-testid="note-cancel"
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleAdd}
              disabled={saving}
              data-testid="note-add"
            >
              {buttonText}
            </Button>
          </Box>
        </>
      )}
    </ParticipantCard>
  )
}

export default ParticipantNote
